import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import { Autom2PlataformaApi } from './Autom2PlataformaApi';
import {
  CapabilitiesDto,
  CdCleanupRequestDto,
  CdCleanupStatusDto,
  CdReferenceDto,
  DeleteInfraLaunchDto,
  InfraCdPreviewDto,
  InfraWorkflowDetailDto,
  InfraWorkflowDto,
  PollerStatusDto,
} from './types';

/** Error carrying the HTTP status, so the UI can tell 403 from a real failure. */
export class Autom2PlataformaRequestError extends Error {
  constructor(readonly status: number, message: string) {
    super(message);
    this.name = 'Autom2PlataformaRequestError';
    Object.setPrototypeOf(this, Autom2PlataformaRequestError.prototype);
  }

  /** True when the caller is authenticated but not allowed to read this. */
  get isForbidden(): boolean {
    return this.status === 403;
  }
}

/**
 * `409` of `POST …/delete-infra` with the CD cleanup `confirmed`: the
 * references changed since the admin reviewed them. Nothing was modified;
 * `references` is the freshly scanned list to review and confirm again.
 */
export class CdReferencesChangedError extends Autom2PlataformaRequestError {
  constructor(readonly references: CdReferenceDto[]) {
    super(409, 'Las referencias de CD han cambiado desde la revisión');
    this.name = 'CdReferencesChangedError';
    Object.setPrototypeOf(this, CdReferencesChangedError.prototype);
  }
}

/**
 * `409` of `POST …/delete-infra` with the CD cleanup `confirmed`: the re-scan
 * could not complete, so nothing was modified. The admin can retry later or
 * destroy the infrastructure with the CD cleanup `skip`ped.
 */
export class CdScanIncompleteError extends Autom2PlataformaRequestError {
  constructor() {
    super(409, 'No se pudo comprobar por completo las referencias de CD');
    this.name = 'CdScanIncompleteError';
    Object.setPrototypeOf(this, CdScanIncompleteError.prototype);
  }
}

/**
 * `fetchApi` adds the Backstage token and `discoveryApi` resolves the plugin's
 * base URL, replacing the hand-rolled `backend.baseUrl` + `identityApi` +
 * `fetch` combination used elsewhere in the app.
 */
export class Autom2PlataformaClient implements Autom2PlataformaApi {
  constructor(
    private readonly discoveryApi: DiscoveryApi,
    private readonly fetchApi: FetchApi,
  ) {}

  private async getBaseUrl(): Promise<string> {
    return this.discoveryApi.getBaseUrl('autom2plataforma');
  }

  private async getJson<T>(path: string): Promise<T> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}${path}`);
    if (!response.ok) {
      throw new Autom2PlataformaRequestError(
        response.status,
        `La petición a autom2plataforma falló (HTTP ${response.status})`,
      );
    }
    return (await response.json()) as T;
  }

  private async postJson<T>(path: string, body: unknown): Promise<T> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      throw new Autom2PlataformaRequestError(
        response.status,
        `La petición a autom2plataforma falló (HTTP ${response.status})`,
      );
    }
    return (await response.json()) as T;
  }

  async listWorkflowsByEntity(entityRef: string): Promise<InfraWorkflowDto[]> {
    const data = await this.getJson<{ items?: InfraWorkflowDto[] }>(
      `/workflows?entityRef=${encodeURIComponent(entityRef)}`,
    );
    return Array.isArray(data.items) ? data.items : [];
  }

  async getWorkflow(workflowId: string): Promise<InfraWorkflowDetailDto> {
    return this.getJson<InfraWorkflowDetailDto>(
      `/workflows/${encodeURIComponent(workflowId)}`,
    );
  }

  async revealSecret(
    workflowId: string,
    path: string,
  ): Promise<Record<string, string>> {
    return this.postJson<Record<string, string>>(
      `/workflows/${encodeURIComponent(workflowId)}/secrets/reveal`,
      { path },
    );
  }

  async getPollerStatus(): Promise<PollerStatusDto> {
    return this.getJson<PollerStatusDto>('/poller/status');
  }

  async getCapabilities(): Promise<CapabilitiesDto> {
    return this.getJson<CapabilitiesDto>('/capabilities');
  }

  async launchDeleteInfra(
    workflowId: string,
    cdCleanup?: CdCleanupRequestDto,
  ): Promise<DeleteInfraLaunchDto> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/workflows/${encodeURIComponent(workflowId)}/delete-infra`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(cdCleanup ? { cdCleanup } : {}),
      },
    );
    if (!response.ok) {
      throw (
        (response.status === 409
          ? await this.cdConflictError(response)
          : undefined) ??
        new Autom2PlataformaRequestError(
          response.status,
          `La petición a autom2plataforma falló (HTTP ${response.status})`,
        )
      );
    }
    return (await response.json()) as DeleteInfraLaunchDto;
  }

  /**
   * The typed error for a 409 whose body names one of the two CD cleanup
   * conflicts, or `undefined` for any other 409 (unreadable body included).
   */
  private async cdConflictError(
    response: Response,
  ): Promise<Autom2PlataformaRequestError | undefined> {
    let body: any;
    try {
      body = await response.json();
    } catch {
      return undefined;
    }
    switch (body?.error?.name) {
      case 'CdReferencesChanged':
        return new CdReferencesChangedError(
          Array.isArray(body.references) ? body.references : [],
        );
      case 'CdScanIncomplete':
        return new CdScanIncompleteError();
      default:
        return undefined;
    }
  }

  async getCdCleanupStatus(workflowId: string): Promise<CdCleanupStatusDto> {
    return this.getJson<CdCleanupStatusDto>(
      `/workflows/${encodeURIComponent(workflowId)}/cd-cleanup`,
    );
  }

  async getDeleteInfraPreview(workflowId: string): Promise<InfraCdPreviewDto> {
    return this.getJson<InfraCdPreviewDto>(
      `/workflows/${encodeURIComponent(workflowId)}/delete-infra/preview`,
    );
  }
}
